import React from 'react';
import { ActionItem, Button, Text, TextButton } from '@wix/wix-base-ui';
import { translate } from '#packages/i18n';
import * as stateManagement from '#packages/stateManagement';
import * as panels from '#packages/panels';
import styles from './pageVariantsPanel.scss';
import type {
  PageVariant,
  CustomSubtitleContent,
} from '@wix/platform-editor-sdk';
import * as platformEvents from 'platformEvents';

import type { MapDispatchToProps, Dispatch } from 'types/redux';
import { hoc } from '#packages/util';

const VARIANTS_ARTICLE_HELP_ID = '70133a53-6e28-4709-a6c3-1b42280b131c';
const PANEL_NAME = 'platformPanels.PageVariantsPanel';

const PANEL_ACTIONS = {
  ADD_VARIANT: 'add-variant',
  HELP_CLICK: 'help-click',
  LEARN_MORE_CLICK: 'learn-more-click',
};

interface PageVariantsPanelDispatchProps {
  openHelpCenter: (helpId: string) => void;
  onClose: () => void;
  onActionItemClick: (
    variant: PageVariant,
    pageManagingAppDefId: string,
  ) => void;
}

interface PageVariantsPanelProps extends PageVariantsPanelDispatchProps {
  panelName: string;
  title: string;
  variants: PageVariant[];
  variantsHelpUrl?: string;
  pageManagingAppDefId: string;
  subtitleData?: CustomSubtitleContent;
  showFooter?: boolean;
  biCallbacks: {
    panelClosed: () => void;
    panelAction: (
      action: string,
      actionId?: string,
      actionName?: string,
    ) => void;
  };
}

export const PageVariantsPanel: React.FC<PageVariantsPanelProps> = ({
  panelName,
  title,
  variants,
  variantsHelpUrl,
  pageManagingAppDefId,
  openHelpCenter,
  onClose,
  onActionItemClick,
  subtitleData,
  showFooter,
  biCallbacks,
}) => {
  const customLearnMoreUrl = variantsHelpUrl ?? subtitleData?.learnMoreUrl;

  return (
    <panels.frames.CustomPanelFrame
      dataHook="page-variant-panel"
      title={title}
      panelName={panelName}
      className={styles.pageVariantsPanel}
      onEscKeyPress={onClose}
      onHelpButtonClick={() => {
        openHelpCenter(VARIANTS_ARTICLE_HELP_ID);
      }}
      onCloseButtonClick={onClose}
      footnote={
        showFooter ? (
          <Text size="small" weight="thin" shouldTranslate={false}>
            {translate('PLATFORM_flow_customization_variant_preset_modal_note')}
          </Text>
        ) : undefined
      }
    >
      <>
        <div className={styles.subtitle}>
          <Text
            dataHook={'subtitle-text'}
            size="medium"
            weight="thin"
            enableEllipsis={false}
            shouldTranslate={false}
          >
            {subtitleData?.text
              ? subtitleData.text
              : translate(
                  'PLATFORM_flow_customization_variant_preset_modal_subtitle',
                )}
          </Text>
          {customLearnMoreUrl ? (
            <TextButton
              dataHook={'learn-more-button'}
              className={styles.linkButton}
              shouldTranslate={false}
              onClick={() => {
                biCallbacks.panelAction(PANEL_ACTIONS.LEARN_MORE_CLICK);
                window.open(customLearnMoreUrl, '_blank');
              }}
            >
              {translate(
                'PLATFORM_flow_customization_variant_preset_modal_subtitle_learn_more',
              )}
            </TextButton>
          ) : null}
        </div>
        {variants.map((variant) => (
          <ActionItem
            dataHook={'variant-item'}
            title={variant.name}
            description={variant.description}
            size="large"
            onClick={() => onActionItemClick(variant, pageManagingAppDefId)}
            action={
              <Button className="btn-inverted btn-sm">
                {translate(
                  'PLATFORM_flow_customization_variant_preset_modal_main_cta',
                )}
              </Button>
            }
            illustration={
              <img className={styles.illustration} src={variant.src} />
            }
            shouldTranslate={false}
          />
        ))}
      </>
    </panels.frames.CustomPanelFrame>
  );
};

const getEditorAPI = (
  dispatch: AnyFixMe,
  getState: AnyFixMe,
  { editorAPI }: AnyFixMe,
) => editorAPI;

const mapDispatchToProps: MapDispatchToProps<
  PageVariantsPanelDispatchProps,
  PageVariantsPanelProps
> = (dispatch: Dispatch, ownProps: PageVariantsPanelProps) => {
  const editorAPI = dispatch(getEditorAPI);
  return {
    openHelpCenter: (helpId: string) => {
      ownProps.biCallbacks.panelAction(PANEL_ACTIONS.HELP_CLICK);
      dispatch(stateManagement.panels.actions.openHelpCenter(helpId));
    },
    onClose: () => {
      editorAPI.panelManager.closePanelByName(PANEL_NAME);
      ownProps.biCallbacks.panelClosed();
    },
    onActionItemClick: (variant: PageVariant, pageManagingAppDefId: string) => {
      ownProps.biCallbacks.panelAction(
        PANEL_ACTIONS.ADD_VARIANT,
        variant.id,
        variant.name,
      );
      const appId =
        editorAPI.platform.getAppDataByAppDefId(
          pageManagingAppDefId,
        ).applicationId;
      editorAPI.platform.notifyApplication(
        appId,
        platformEvents.factory.pageVariantSelected({
          variantId: variant.id,
        }),
      );
      editorAPI.panelManager.closePanelByName(PANEL_NAME);
    },
  };
};

export default hoc.connect(
  hoc.STORES.EDITOR_API,
  null,
  mapDispatchToProps,
)(PageVariantsPanel);
